
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Zap, Users, Bot, BarChart3 } from 'lucide-react';
import { OnboardingStepProps } from './types';

export interface WelcomeStepProps extends OnboardingStepProps {}

export const WelcomeStep: React.FC<WelcomeStepProps> = ({
  onComplete,
  isLoading = false
}) => {
  const features = [
    {
      icon: Users,
      title: 'Smart Candidate Management',
      description: 'AI-powered candidate tracking and matching'
    },
    {
      icon: Bot,
      title: 'AI Agents',
      description: 'Automated screening, emails, and scheduling'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Track performance and gain insights'
    },
    {
      icon: Zap,
      title: 'Automation',
      description: 'Streamline your recruitment workflow'
    }
  ];

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Welcome to InvenioCRM
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Your AI-powered recruiting platform that transforms how you find, engage, and hire top talent. 
          Let's get you set up in just a few minutes.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features.map((feature, index) => (
          <Card key={index} className="border-2 hover:border-primary/20 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <div className="text-center space-y-3">
          <Zap className="h-8 w-8 text-blue-600 mx-auto" />
          <h3 className="font-semibold text-gray-900">Ready to Transform Your Recruiting?</h3>
          <p className="text-gray-600">
            This quick setup will configure your profile, organization, and AI agents so you can start recruiting smarter, not harder.
          </p>
        </div>
      </div>

      <div className="text-center">
        <Button onClick={onComplete} disabled={isLoading} size="lg" className="px-8">
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            'Get Started'
          )}
        </Button>
      </div>
    </div>
  );
};
